import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { Money } from '@/graphql/saleor/api';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './FinanceCalculator.styles';
import { messages } from './messages';
export interface CembraFinanceCalculatorProps {
  className?: string;
  monthlyLoanTerm?: number;
  price: Money;
  selectMonthlyLoanTerm?: (value: number) => void;
}
export const CembraFinanceCalculator = ({
  className,
  monthlyLoanTerm: _monthlyLoanTerm,
  price,
  selectMonthlyLoanTerm,
}: CembraFinanceCalculatorProps) => {
  const { formatPrice } = useRegionProvider();
  const { formatMessage } = useIntl();
  const [monthlyLoanTerm, setMontlyLoanTerm] = useState(_monthlyLoanTerm ?? 12);
  const months = [12, 24, 36, 48];

  const { cembraFinancingPaymentMax } = useConfigProvider();

  useEffect(() => {
    selectMonthlyLoanTerm && selectMonthlyLoanTerm(monthlyLoanTerm);
  }, [monthlyLoanTerm]);

  const handleMontlyLoanTerm = (value: number) => {
    setMontlyLoanTerm(value);
  };

  return (
    <S.Container
      className={className}
      data-test-id='CembraFinanceCalculator-Container'
    >
      <Flex>
        {months.map((el) => (
          <S.TermButton
            key={el}
            data-testid={`financeCalculatorTerm${el}`}
            isMobile={false}
            isSelected={el === monthlyLoanTerm}
            onClick={() => handleMontlyLoanTerm(el)}
          >
            <Typography>{el}</Typography>
            <Typography>{formatMessage(messages.months)}</Typography>
          </S.TermButton>
        ))}
      </Flex>

      <S.MonthlyPaymentsBox marginBottom='s100' marginTop='s100'>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.totalAmountPayable)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {formatPrice(price)}
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.yourProvider)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.cembrapay)}
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.monthlyLoanTerm)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {monthlyLoanTerm} {formatMessage(messages.months)}
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography
            fontWeight='semibold'
            scale='h5'
            textTransform='uppercase'
          >
            {formatMessage(messages.yourMonthlyPayments)}
          </Typography>
          <Typography
            fontWeight='semibold'
            scale='h5'
            textTransform='uppercase'
          >
            {formatPrice({
              amount:
                Math.min(price.amount, cembraFinancingPaymentMax) /
                monthlyLoanTerm,
              currency: price.currency,
            })}
          </Typography>
        </Flex>
      </S.MonthlyPaymentsBox>
    </S.Container>
  );
};
